<template>
  <FormCard
    v-for="fieldGroup in props.fieldGroups"
    :key="fieldGroup.key"
    :data-testid="`self-service-form-facebook-group-${fieldGroup.key}`"
    :data-group-key="fieldGroup.key"
    :title="fieldGroup.name"
    :class="{ 'selected-card': openCard === fieldGroup.key }"
    :is-open="openCard === fieldGroup.key"
    @toggle="handleFormCardToggle(fieldGroup.key)"
    :completed="completedCards.has(fieldGroup.key)"
    @enter-pressed="handleSaveAndContinue(fieldGroup.key)">
    <template #form-content>
      <div
        class="form-section"
        v-for="field in fieldGroup.properties"
        :key="field.key"
        :data-testid="`self-service-field-${field.key}`"
        :data-field-key="field.key">
        <InstagramPageSelect
          v-if="field.type === 'select' && field.key === 'instagram_actor_id'"
          v-model="obj[field.key]"
          :facebook_page_id="(obj as FacebookObject)?.page_id ?? ''"
          :campaign_id="providerId?.toString() ?? ''"
          :field="field"
          :errors="errors[field.key] || null" />

        <FacebookTargeting
          v-else-if="field.type === 'select' && field.key === 'target_locations'"
          v-model="obj[field.key]"
          :campaignId="providerId ? Number(providerId) : 0"
          :available-countries="availableCountries"
          :errors="errors[field.key] || null" />

        <FacebookAds
          v-else-if="field.type === 'ads'"
          v-model="obj[field.key]"
          :campaignId="providerId ? Number(providerId) : 0"
          :errors="errors[field.key] || {}" />

        <LeadSelect
          v-else-if="field.type === 'select'"
          v-model="obj[field.key]"
          :options="field.options || []"
          :label="field.label"
          :errors="errors[field.key] || null"
          :empty-message="field.key === 'page_id' ? $t('campaigns.facebook.no_fb_pages_message') : undefined" />

        <LeadInput
          v-else-if="field.type === 'text'"
          v-model="obj[field.key]"
          :label="field.label"
          :errors="errors[field.key] || null" />
      </div>
      <div
        class="form-actions float-right"
        :data-testid="`self-service-save-action-${fieldGroup.key}`"
        :data-i18n-key="isLast(fieldGroup.key) ? 'btn.save' : 'btn.save_and_continue'">
        <LeadButton variant="secondary" :loading="loadingSave" @click="handleSaveAndContinue(fieldGroup.key)">
          {{ isLast(fieldGroup.key) ? $t('btn.save') : $t('btn.save_and_continue') }}
          <template v-if="!isLast(fieldGroup.key)" #icon>
            <IconArrowBigRightFilled size="16" stroke-width="1.5" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script setup lang="ts">
import { watch, ref, onMounted, computed, nextTick } from 'vue';
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import LeadSelect from '@/components/form/LeadSelect.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import { IconArrowBigRightFilled } from '@tabler/icons-vue';
import InstagramPageSelect from '@/components/form/InstagramPageSelect.vue';
import { updateCampaignObject, getAvailableCountries } from '@/api/campaign';
import type { IFacebookCountry } from '@/types/locationTargeting';
import type { FieldGroup } from '@/types/types';
import FacebookTargeting from '@/components/form/FacebookTargeting.vue';
import FacebookAds from '@/components/form/FacebookAds.vue';
import { type Ad, type AdPlacement } from '@/types/types';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';
import translator from '@/locale/translator';

const props = defineProps<{
  providerId: string | null | undefined;
  fieldGroups: FieldGroup[];
  object: any;
}>();

interface FacebookObject {
  page_id: string;
  instagram_actor_id: string;
  target_locations: Array<{
    target_country: string | undefined;
    target_label: string | undefined;
    target_name: string | undefined;
    target_key: string | undefined;
    target_type: string | undefined;
    target_radius: number | undefined;
    target_radius_metric_unit: string;
  }>;
  ads: Ad[];
}

const openCard = ref<string | null>(null);
const availableCountries = ref<IFacebookCountry[]>([]);
const query = ref('');
const providerStore = useProviderStore();
const { completedDecks, obj } = storeToRefs(providerStore);
const loadingSave = ref(false);

const emit = defineEmits<{
  (e: 'update:object', obj: any): void;
}>();

const errors = ref({});

onMounted(async () => {
  const countries = await getAvailableCountries(props.providerId ?? '', query.value);
  availableCountries.value = countries.data;

  // Open the first card by default if field groups are available and there is not already an open card
  if (props.fieldGroups.length > 0 && !openCard.value) {
    openCard.value = props.fieldGroups[0].key;
  }
});

const completedCards = computed(() => {
  if (!props.providerId) return new Set<string>();
  return completedDecks.value[props.providerId] || new Set<string>();
});

const handleFormCardToggle = (key: string) => {
  // If the clicked card is already open, close it
  if (openCard.value === key) {
    openCard.value = null;
  } else {
    // Otherwise, open the clicked card (and close any other open card)
    openCard.value = key;
  }
};

const handleSaveAndContinue = async (currentCard: string) => {
  errors.value = {};
  loadingSave.value = true;
  // Update the object with the values of the current card
  const updateData = {};
  props.object.groups.forEach((group) => {
    if (group.key === currentCard) {
      Object.values(group.properties).map((property: any) => {
        if (obj.value[property.key] !== undefined) {
          updateData[property.key] = obj.value[property.key];

          if (updateData['ads']) {
            const adsArray = updateData['ads'] as Ad[];
            adsArray.forEach((ad: Ad) => {
              const hasUnselectedStoryPlacement = ad.ad_placements.some(
                (p: AdPlacement) => p.placement_group === 'stories_and_reels' && p.image_id === 0
              );
              if (hasUnselectedStoryPlacement) {
                errors.value['ads'] = [translator.get('campaigns.facebook.facebook_ad_story_error')];
              }
            });
          }
        }
      });
    }
  });

  if (Object.keys(errors.value).length > 0) {
    return;
  }

  try {
    const res = await updateCampaignObject(props.providerId ?? '', updateData);
    emit('update:object', res.data);

    // Clear any previous errors on success
    errors.value = {};
    setTimeout(() => {
      loadingSave.value = false;
      providerStore.addCompletedCard(currentCard, props.providerId ?? '');

      // If there's a next card to open, set it as the open card
      const currentIndex = props.fieldGroups.findIndex((group) => group.key === currentCard);
      if (currentIndex >= 0 && currentIndex < props.fieldGroups.length - 1) {
        openCard.value = props.fieldGroups[currentIndex + 1].key;
      } else {
        // If no next card, close the current card
        openCard.value = null;
      }
    }, 500);
  } catch (error: any) {
    if (error.responseJson) {
      errors.value = error.responseJson.errors?.provider_values || {};
    } else {
      console.error('Unexpected error:', error);
    }
    loadingSave.value = false;
  }
};

const isLast = (key: string) => {
  const lastIndex = props.fieldGroups.length - 1;
  return props.fieldGroups[lastIndex].key === key;
};

watch(
  () => props.fieldGroups,
  (newValue) => {
    if (newValue) {
      openCard.value = null;
      if (props.fieldGroups.length > 0) {
        if (!openCard.value)
          nextTick(() => {
            openCard.value = props.fieldGroups[0].key;
          });

        if (props.object?.groups) {
          props.object.groups.map((group) => {
            if (props.object?.confirmed || props.object?.values_valid) {
              providerStore.addCompletedCard(group.key, props.providerId ?? '');
            }

            if (group.properties) {
              Object.values(group.properties).map((property: any) => {
                obj.value[property.key] = property.value || '';
              });
            }
          });
        }
      }
    }
  },
  { immediate: true }
);

// watch for changes in the facebook page id to reset the instagram actor id
watch(
  () => (obj.value as any)?.page_id,
  (newPageId, oldPageId) => {
    if (newPageId !== oldPageId && obj.value) {
      (obj.value as FacebookObject).instagram_actor_id = '';
    }
  }
);
</script>

<style scoped lang="scss">
.float-right {
  float: right;
}

.form-section {
  margin-bottom: 30px;
}
</style>
